import React from 'react';
import { Link } from 'react-router-dom';
import './PersonalVaultPrivacyPage.css';

const PersonalContactPrivacyPage: React.FC = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <section className="privacy-hero">
          <div className="privacy-hero-row">
            <div>
              <h1>Privacy Policy</h1>
              <div className="privacy-meta">Personal Contact — Privacy Policy</div>
            </div>
            <div className="privacy-hero-right">
              <div className="privacy-badge">Version 1.0</div>
              <div className="privacy-updated">Last Updated: September 6, 2026</div>
            </div>
          </div>
        </section>

        <article className="privacy-card">
          <h2>Privacy Policy</h2>
          <p>Personal Contact is built on a single principle: <strong>your contacts and context belong to you.</strong></p>
          <p>
            Please also read our{' '}
            <Link to="/personal-contact/terms">Terms of Service</Link>, which govern your use of the App.
          </p>

          <h2>1. Local Data Storage</h2>
          <p>User data is stored locally on your device. We do not upload, transmit, or store your Personal Contact data on our servers.</p>
          <p>
            Data you create in Personal Contact — contacts, relationships, activities, interaction history,
            notes, map-related context you add, and any locally generated suggestions — is stored on your device.
            We do not maintain servers or accounts for receiving this content.
          </p>
          <ul>
            <li><strong>No accounts required</strong>: The app operates without requiring registration or login</li>
            <li><strong>Local storage</strong>: When you close the app, your data remains on your device</li>
            <li><strong>Optional backups you control</strong>: If you enable a backup, the file goes to a destination you choose. We do not receive, access, or store backup files</li>
          </ul>
          <p>
            Because the App does not collect or transmit your in-app content, we cannot recover lost data
            or access encrypted content. See our <Link to="/personal-contact/terms">Terms of Service</Link> for
            user responsibilities regarding backups, app lock, and passphrase management.
          </p>

          <h2>2. Contacts, Activities, and Interaction Frequency</h2>
          <p>
            Personal Contact lets you add contacts and record activities with them so you can see how often
            you stay in touch. This tracking is created by you and stored only on your device.
          </p>
          <ul>
            <li>We do not sync your contact book or activity log to SynorLab servers</li>
            <li>We do not build a cloud social graph from your network</li>
            <li>Frequency and relationship insights are computed locally from records you add</li>
          </ul>

          <h2>3. On-Device AI (Local LLM)</h2>
          <p>
            If you use on-device AI features, a language model running <strong>on your device</strong> may
            analyze your local notes and activity records to help organize topics for a future conversation.
          </p>
          <ul>
            <li>Contact names, notes, and activity details used for this analysis stay on your device</li>
            <li>We do not send this content to a cloud AI API or to SynorLab</li>
            <li>Model files you import remain under your control on the device</li>
          </ul>
          <p>
            Suggestions generated by a local model are optional helpers. They are not a substitute for your
            own judgment and may be incomplete or inaccurate.
          </p>

          <h2>4. Encryption, App Lock, and Backups</h2>
          <p>
            You may optionally enable encryption, an app lock (including biometric unlock where the device
            supports it), and backups.
          </p>
          <ul>
            <li>A passphrase or lock you set is stored using the device&apos;s security features and is <strong>never transmitted</strong> to us</li>
            <li>Without your passphrase or unlock method, encrypted data cannot be accessed by anyone, including us</li>
            <li>Exported or backup files go directly to a location you select</li>
          </ul>

          <h2>5. Advertising and Third-Party Services (AdMob)</h2>
          <p>The App may use Google AdMob to display advertisements. While we do not collect or transmit any personal or app-stored contact data, AdMob may independently collect certain device or usage information for delivering, measuring, and improving ads.</p>
          <p>AdMob may collect information such as device identifiers, approximate location, IP address, and ad interaction data. This data collection is governed solely by <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google&apos;s Privacy Policy</a> and is not used by us for any purpose.</p>
          <p><strong>We do not share contacts, activities, notes, or local AI content with AdMob or any third party.</strong></p>
          <p>Users may opt out of personalized ads using the device&apos;s system settings or <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Google ad preferences</a>. Disabling personalized ads does not affect core app functionality.</p>

          <h2>6. Device Permissions</h2>
          <p>Personal Contact may request the following permissions, only when a feature needs them:</p>
          <ul>
            <li><strong>Location</strong>: To place or view contacts on a map when you choose to use map features (stored locally)</li>
            <li><strong>Storage / Files</strong>: To export or import backups to a location you choose</li>
            <li><strong>Biometric Authentication</strong>: To unlock the app if you enable app lock (optional)</li>
          </ul>
          <p>These permissions are used <strong>exclusively for local operations</strong> — your contact and activity content is not transmitted to us.</p>

          <h2>7. Changes to This Policy</h2>
          <p>If we make changes to this Privacy Policy, we will update the &quot;Last Updated&quot; date. Our core principle remains unchanged: <strong>your contacts and context belong to you, and we will never collect your in-app content.</strong></p>

          <h2>Summary</h2>
          <p>We do not collect or transmit your contacts, activities, notes, or local AI analysis. Everything stays on your device unless you export or back it up to a destination you control. AdMob may collect device-level data for advertising; we do not share your app content with AdMob or any third party.</p>

          <footer className="privacy-footer">
            <div>© Personal Contact — All rights reserved.</div>
          </footer>
        </article>

        <div className="privacy-back">
          <Link to="/personal-contact" className="privacy-back-link">← Back to Personal Contact</Link>
        </div>
      </div>
    </div>
  );
};

export default PersonalContactPrivacyPage;
